import { FC, useCallback, useState } from 'react'
import { Box, Snackbar, Alert as MuiAlert } from '@mui/material'
import { fetchEventArticles, addEventArticle, updateEventArticle, deleteEventArticle } from '@/features/news/newsThunks'
import EventsTable from '../events/EventsTable'
import { useSnackbar } from '@/hooks/useSnackbar'
import { useFormManager } from '@/hooks/useFormManager'
import { parseDateToDayjs } from '@/utils/parseDateToDayjs'
import { EventArticle } from '@/types'
import EventsForm from '../events/EventsForm'
import { EventsFormValues, INITIAL_EVENTS_VALUES } from '../events/eventsForm.constants'
import { useDispatch } from 'react-redux'
import { AppDispatch } from '@/app/store'
import ConfirmDialog from '@/components/common/ConfirmDialog'
import LoadingOverlay from '@/components/common/LoadingOverlay'
import { toColombianDate } from '@/utils/colombianTimezone'

const transformEventPayload = (values: EventsFormValues): any => {
  return {
    title: values.title,
    place: values.place,
    manager: values.manager,
    site: values.site,
    date: toColombianDate(values.date),
  }
}

const EventsManager: FC = () => {
  const dispatch = useDispatch<AppDispatch>()
  const snackbar = useSnackbar()
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [eventToDelete, setEventToDelete] = useState<EventArticle | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)

  const {
    values,
    editingId,
    isSubmitting,
    setFieldValue,
    handleEdit,
    handleReset,
    handleSubmit,
    SubmittingOverlay,
  } = useFormManager<EventsFormValues>({
    initialValues: INITIAL_EVENTS_VALUES,
    onAddThunk: (payload: any) => addEventArticle(payload),
    onUpdateThunk: (id: string, payload: any) => updateEventArticle({ id, data: payload }),
    onFetchThunk: fetchEventArticles,
    transformPayload: transformEventPayload,
  })

  const handleEditEvent = useCallback(
    (article: EventArticle) => {
      const mappedArticle: EventsFormValues = {
        id: article.id || '',
        title: article.title || '',
        place: article.place || '',
        manager: article.manager || 'Docentes',
        site: article.site || 'Sede Principal',
        date: article.date ? parseDateToDayjs(article.date) : null,
      }
      handleEdit(mappedArticle, [])
      window.scrollTo({ top: 600, behavior: 'smooth' })
    },
    [handleEdit]
  )

  const handleDeleteEvent = useCallback(
    (event: EventArticle) => {
      if (!event.id) {
        snackbar.show('Error: ID de evento no válido', 'error')
        return
      }
      setEventToDelete(event)
      setDeleteDialogOpen(true)
    },
    [snackbar]
  )

  const confirmDelete = useCallback(async () => {
    if (!eventToDelete?.id) {
      setDeleteDialogOpen(false)
      return
    }

    setIsDeleting(true)
    try {
      await dispatch(deleteEventArticle(eventToDelete.id)).unwrap()
      snackbar.show('Evento eliminado correctamente', 'success')
      setDeleteDialogOpen(false)
      setEventToDelete(null)
    } catch (error: any) {
      snackbar.show(error || 'Error al eliminar el evento', 'error')
      setDeleteDialogOpen(false)
      setEventToDelete(null)
    } finally {
      setIsDeleting(false)
    }
  }, [dispatch, snackbar, eventToDelete])

  const cancelDelete = useCallback(() => {
    setDeleteDialogOpen(false)
    setEventToDelete(null)
  }, [])

  const validateFormData = useCallback(
    (): { valid: boolean; error?: string } => {
      if (!values.title || !values.title.trim()) {
        return { valid: false, error: 'Por favor ingresa el título' }
      }
      if (!values.manager || !values.manager.trim()) {
        return { valid: false, error: 'Por favor ingresa el responsable' }
      }
      if (!values.site || !values.site.trim()) {
        return { valid: false, error: 'Por favor ingresa la sede' }
      }
      if (values.date == null) {
        return { valid: false, error: 'Por favor ingresa la fecha' }
      }
      if (!values.place || !values.place.trim()) {
        return { valid: false, error: 'Por favor ingresa el lugar' }
      }
      return { valid: true }
    },
    [values.title, values.manager, values.site, values.date, values.place]
  )

  const handleEventsSubmit = useCallback(
    async () => {
      const validation = validateFormData()
      if (!validation.valid) {
        snackbar.show(validation.error!, 'error')
        return
      }

      const onSubmit = handleSubmit(
        (isEditing: boolean) => {
          snackbar.show(
            isEditing ? 'Evento actualizado correctamente' : 'Evento agregado correctamente',
            'success'
          )
        },
        (errorMsg: string) => snackbar.show(errorMsg, 'error')
      )
      await onSubmit()
    },
    [handleSubmit, snackbar, validateFormData]
  )

  return (
    <Box>
      {SubmittingOverlay()}
      {isDeleting && <LoadingOverlay message="Eliminando evento, por favor espere..." />}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={snackbar.close}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <MuiAlert onClose={snackbar.close} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </MuiAlert>
      </Snackbar>

      <ConfirmDialog
        open={deleteDialogOpen}
        title="Confirmar eliminación"
        message="¿Estás seguro de que quieres eliminar el evento"
        itemName={eventToDelete?.title}
        warningMessage="Esta acción no se puede deshacer."
        confirmText="Eliminar"
        cancelText="Cancelar"
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
        confirmColor="error"
        isLoading={isDeleting}
      />

      <EventsForm
        values={values}
        editingId={editingId}
        isSubmitting={isSubmitting}
        setFieldValue={setFieldValue}
        onSubmit={handleEventsSubmit}
        onReset={handleReset}
      />

      <EventsTable onEdit={handleEditEvent} onDelete={handleDeleteEvent} />
    </Box>
  )
}

export default EventsManager
